import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAppKitConnection } from '@reown/appkit-adapter-solana/react';
import { modal } from '@reown/appkit/react';

export default function Signup() {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API;
  
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [walletConnected, setWalletConnected] = useState(false);
  const [optsending, setOptSending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [emailDuplicateError, setEmailDuplicateError] = useState('');

  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    otp: "",
    password: "",
    phantomSolanaAddress: "",
  });
  
  const [errors, setErrors] = useState({});

  const {
    connected,
    connecting,
    disconnecting,
    disconnect,
    error: walletError,
  } = useAppKitConnection('solana');
  
  const [status, setStatus] = useState('disconnected');
  const [walletAddress, setWalletAddress] = useState('');

    // Function to read from localStorage
  const readStatusAndAddressFromCache = () => {
    const storedStatus = localStorage.getItem('@appkit/connection_status');
    setStatus(storedStatus || 'disconnected');

    const rawBalanceCache = localStorage.getItem('@appkit/native_balance_cache');
    if (!rawBalanceCache) {
      setWalletAddress('');
      return;
    }

    try {
      const parsedCache = JSON.parse(rawBalanceCache);
      const keys = Object.keys(parsedCache);
      if (keys.length === 0) {
        setWalletAddress('');
        return;
      }
      const firstKey = keys[0];
      const parts = firstKey.split(':');
      const address = parts[parts.length - 1];
      setWalletAddress(address);
      console.log('Extracted Wallet Address:', address);
    } catch (e) {
      console.error('Failed to parse native_balance_cache:', e);
      setWalletAddress('');
    }
  };

  // Initial read and setup polling
  useEffect(() => {
    readStatusAndAddressFromCache();
    
    // Set up polling to check for changes every second
    const intervalId = setInterval(readStatusAndAddressFromCache, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Also sync when connection state changes
  useEffect(() => {
    readStatusAndAddressFromCache();
  }, [connected, connecting, disconnecting]);

  // Listen for external localStorage changes
  useEffect(() => {
    function onStorage(event) {
      if (
        event.key === '@appkit/connection_status' ||
        event.key === '@appkit/native_balance_cache'
      ) {
        console.log('Storage event detected, updating status/address');
        readStatusAndAddressFromCache();
      }
    }

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    // Check if device is mobile
    setIsMobile(/android|iphone|ipad|ipod/i.test(navigator.userAgent));

    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/countries`);

          setCountries(response.data.sort((a, b) => a.name.localeCompare(b.name)));

      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
  
    fetchCountries();
  
    const fetchIpAddress = async () => {
      try {
        const response = await axios.get("https://api64.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };
  
    fetchIpAddress();
  }, []);

const checkEmailDuplicate = async (email) => {
  try {
    const response = await axios.get(`${baseUrl}/api/check-email`, {
      params: { email },
    });

    if (response.data.exists) {
      setEmailDuplicateError('Email already registered');
    } else {
      setEmailDuplicateError('');
    }
  } catch (error) {
    console.error('Error checking email:', error);
    setEmailDuplicateError('');
  }
};


  
  const handleManualWalletInput = (e) => {
    const address = e.target.value;
    setFormData({ ...formData, phantomSolanaAddress: address });
    
    // Basic validation - you might want to add more robust validation
    if (address.length > 30) {
      setWalletConnected(true);
    } else {
      setWalletConnected(false);
    }
  };

  const connectPhantomWallet = async (e) => {
    e.preventDefault();
  
    const isPhantomInjected = window.solana && window.solana.isPhantom;
  
    try {
      if (isPhantomInjected) {
        const response = await window.solana.connect();
        const address = response.publicKey.toString();
  
        const rpcResponse = await fetch("https://api.mainnet-beta.solana.com", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getAccountInfo",
            params: [address, { encoding: "base64" }],
          }),
        });
  
        const data = await rpcResponse.json();
  
        if (data?.result?.value === null) {
          toast.error("Address not found on mainnet. Please switch to mainnet in Phantom.");
          return;
        }
  
        setFormData({ ...formData, phantomSolanaAddress: address });
        setWalletConnected(true);
        toast.success("Successfully connected to Phantom Wallet on Mainnet.");
      } else if (isMobile && !isPhantomInjected) {
        // For mobile without Phantom, we'll let them enter manually
        return;
      } else {
        toast.error("Phantom Wallet not found. Please install it.");
      }
    } catch (error) {
      console.error("Wallet connection failed", error);
      toast.error("Failed to connect wallet.");
    }
  };

  const handleConnect = async () => {
    try {
      if (connected) {
        await disconnect();
      } else {
        modal.open('solana');
      }
    } catch (err) {
      console.error('Connection error:', err);
    }
  };
  useEffect(() => {
  if (walletAddress) {
    setFormData((prev) => ({ ...prev, phantomSolanaAddress: walletAddress }));
  }
}, [walletAddress]);

  
  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };

  const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData({ ...formData, [name]: value });

  if (name === "email") {
    checkEmailDuplicate(value);
  }
};

  const handleSendOtp = async () => {
    setOptSending(true);
    if (!formData.email) {
      setErrors({ ...errors, email: "Email is required" });
      return;
    }
  
    setErrors({ ...errors, email: "" });
  
    try {
      const response = await axios.post(`${baseUrl}/auth/send-otp`, {
        email: formData.email,
      });
  
      if (response.data) {
        toast.success("OTP sent successfully.");
      }
      setOptSending(false);

    } catch (error) {
      console.error("Error sending OTP:", error.response?.data || error.message);
      setOptSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!formData.firstName) newErrors.firstName = "First Name is required";
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    if (!selectedCountry) newErrors.country = "Country is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
    }

    setErrors({});

    if (emailDuplicateError) {
      toast.error("Email is already registered.");
     return;
    }


    try {
        // Step 1: Verify OTP before registration
        // const otpResponse = await axios.post(`${baseUrl}/auth/verify-otp`, {
        //     email: formData.email,
        //     otp: formData.otp,
        // });

        // if (!otpResponse.data.otpVerified) {
        //     toast.error("Invalid OTP. Please try again.");
        //     return;
        // }

        // Step 2: Proceed with registration if OTP is valid
        const requestData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            country: selectedCountry,
            email: formData.email,
            password: formData.password,
            // phantomSolanaAddress: formData.phantomSolanaAddress,
            ipAddress: ipAddress,
        };

        const response = await axios.post(`${baseUrl}/auth/register`, requestData, {
            headers: { "Content-Type": "application/json" },
        });

        toast.success("Registration successful!");
        navigate("/token/signin");

    } catch (error) {
        const errorMessage = error.response?.data?.message || "Registration failed!";
        toast.error(errorMessage);
        console.error("Registration failed:", errorMessage);
    }
  };

  return (
    <>
      <div
        style={{
          backgroundImage: `url(/images/main-background.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: -1,
          filter: "blur(10px)",
        }}
      ></div>
      <div className="bg-overlay"></div>

      <div className="signup-container" style={{ marginTop: '80px' }}>
        <div className="signup-wrapper">
          <div className="signup-card">
            <h1 className="signup-title">Create Your Account</h1>
            <p className="signup-subtitle">
              Join us today! Please enter your details.
            </p>

            <form onSubmit={handleSubmit} className="signup-form">
              {/* First Name */}
              <div className="form-group">
                <div className="input-with-icon">
                  <Icon icon="mdi:account-outline" className="input-icon" />
                  <input
                    type="text"
                    name="firstName"
                    className="form-input"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                {errors.firstName && <p className="error-message">{errors.firstName}</p>}
              </div>

              {/* Last Name */}
              <div className="form-group">
                <div className="input-with-icon">
                  <Icon icon="mdi:account-outline" className="input-icon" />
                  <input
                    type="text"
                    name="lastName"
                    className="form-input"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
                {errors.lastName && <p className="error-message">{errors.lastName}</p>}
              </div>

              {/* Country */}
              <div className="form-group">
                <div className="input-with-icon">
                  <Icon icon="mdi:earth" className="input-icon" />
                  <select
                    className="form-input"
                    onChange={handleCountryChange}
                    value={selectedCountry}
                    name="country"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.country && <p className="error-message">{errors.country}</p>}
              </div>

              {/* Email */}
              <div className="form-group">
                <div className="input-with-icon">
                  <Icon icon="mdi:email-outline" className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && <p className="error-message">{errors.email}</p>}
                  {emailDuplicateError && <p className="error-message" style={{ color: 'red' }}>{emailDuplicateError}</p>}

              </div>

              {/* Password */}
              <div className="form-group password-group">
                <div className="input-with-icon password-input-container">
                  <Icon icon="mdi:lock-outline" className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-input"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <span
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} />
                  </span>
                </div>
                {errors.password && <p className="error-message">{errors.password}</p>}
                <p className="password-hint">Your password must have at least 8 characters.</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                // disabled={!formData.phantomSolanaAddress}
                className="submit-button"
              >
                Create Account
              </button>
            </form>

            <div className="login-redirect">
              <p>
                Already have an account?{" "}
                <Link to="/token/signin" className="login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .signup-container {
          width: 100%;
          min-height: calc(100vh - 80px);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
          box-sizing: border-box;
        }
        
        .signup-wrapper {
          width: 100%;
          max-width: 500px;
        }
        
        .signup-card {
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .signup-title {
          font-size: 2rem;
          color: #333;
          text-align: center;
          margin-bottom: 10px;
        }
        
        .signup-subtitle {
          color: #666;
          text-align: center;
          margin-bottom: 30px;
        }
        
        .form-group {
          margin-bottom: 20px;
          position: relative;
        }
        
        .input-with-icon {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          font-size: 1.2rem;
          z-index: 2;
        }
        
        .form-input {
          width: 100%;
          padding: 15px 15px 15px 45px;
          border-radius: 8px;
          border: 1px solid #ddd;
          font-size: 1rem;
          box-sizing: border-box;
          position: relative;
          z-index: 1;
        }
        
        select.form-input {
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 16px center;
          background-size: 1em;
          padding-right: 40px;
          border: 1px solid #ddd;
        }
        
        .otp-group {
          position: relative;
        }
        
        .otp-button {
          position: absolute;
          right: 17px;
          top: 7px;
          background-color: #f0b90b;
          border: none;
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
          z-index: 2;
        }
        
        .password-input-container {
          position: relative;
        }
        
        .password-toggle {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #666;
          z-index: 2;
        }
        
        .password-hint {
          font-size: 0.8rem;
          color: #666;
          margin-top: 5px;
          text-align: left;
          padding-left: 10px;
        }
        
        .wallet-hint {
          font-size: 0.8rem;
          color: #666;
          margin-top: 5px;
          text-align: left;
          padding-left: 10px;
        }
        
        .wallet-button {
          width: 100%;
          padding: 15px;
          border-radius: 8px;
          border: none;
          background-color: #f0b90b;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          margin-bottom: 15px;
          transition: background-color 0.3s;
        }
        
        .wallet-button:hover {
          background-color: #e0a800;
        }
        
        .wallet-button.connected {
          background-color: #28a745;
        }
        
        .wallet-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .submit-button {
          width: 100%;
          padding: 15px;
          border-radius: 8px;
          border: none;
          background-color: #f0b90b;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          margin-top: 10px;
          transition: background-color 0.3s;
        }
        
        .submit-button:hover {
          background-color: #e0a800;
        }
        
        .submit-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .login-redirect {
          text-align: center;
          margin-top: 20px;
          color: #666;
        }
        
        .login-link {
          color: #f0b90b;
          text-decoration: none;
          font-weight: 600;
        }
        
        .login-link:hover {
          text-decoration: underline;
        }
        
        .error-message {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 5px;
          text-align: left;
          padding-left: 10px;
        }
        
        .wallet-address {
          font-size: 0.8rem;
          color: #666;
          word-break: break-all;
          margin-top: -10px;
          margin-bottom: 15px;
        }
        
        @media (max-width: 768px) {
          .signup-card {
            padding: 20px;
          }
          
          .signup-title {
            font-size: 1.5rem;
          }
          
          .form-input {
            padding: 12px 12px 12px 40px;
          }
          
          .input-icon {
            display:none;
          }
          
          select.form-input {
            padding-right: 25px;
          }
        }
        
        @media (max-width: 480px) {
          .signup-container {
            padding: 10px;
          }
          
          .signup-card {
            padding: 15px;
          }
          
          .form-input {
            padding: 10px 10px 10px 36px;
          }
          
          .input-icon {
            display:none;
          }
          
          .otp-button {
            right: 12px;
            top: 12px;
            padding: 6px 10px;
            font-size: 0.8rem;
          }
          
          .password-toggle {
            right: 10px;
          }
          
          select.form-input {
            padding-right: 20px;
          }
        }
      `}</style>
    </>
  );
}

